import React from "react";
import { useQuery } from "@apollo/client";
import { GET_BILLING_DATA } from "@ugg/shared/api/graphql/queries";
import { useUserPremiumState } from "@ugg/shared/api/requests/accounts/user-state";
import AccountStatus from "./AccountStatus";
import BillingInformation from "./BillingInformation";
import BillingHistory from "./BillingHistory";

interface Invoice {
  currency?: string | null;
  paid?: boolean | null;
  brand?: string | null;
  last4?: string | null;
  periodStart?: number | null;
  periodEnd?: number | null;
  totalPaid?: number | null;
}

interface Subscription {
  cancelAtPeriodEnd?: boolean | null;
  currentPeriodEnd?: number | null;
  status?: string | null;
  isBraintree?: boolean | null;
}

interface UpcomingInvoice {
  chargeAmount?: number | null;
  subtotal?: number | null;
  error?: string | null;
  nextPaymentAttempt?: number | null;
}

interface BillingDataResponse {
  getBillingData?: {
    invoices?: Invoice[] | null;
    subscription?: Subscription | null;
    upcomingInvoice?: UpcomingInvoice | null;
  } | null;
}

interface CreditCard {
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  isDefault?: boolean | null;
  name?: string | null;
}

interface PayPalInfo {
  email?: string | null;
}

interface BoostBillingOverviewProps {
  userSettings?: {
    billingSettings?: {
      creditCards?: CreditCard[] | null;
      paypal?: PayPalInfo | null;
    } | null;
  } | null;
}

const BoostBillingOverview: React.FC<BoostBillingOverviewProps> = ({ userSettings }) => {
  // Get user premium state
  const { data: premiumData } = useUserPremiumState();
  const { isPremium, premiumVersion, isOnFreeTrial } = premiumData || {};

  // Fetch legacy billing data
  const { data: billingData, loading: billingLoading } = useQuery<BillingDataResponse>(GET_BILLING_DATA, {
    skip: !isPremium || premiumVersion === "plus",
  });

  // Only render for legacy BOOST users (not PLUS users)
  if (!isPremium || premiumVersion === "plus") {
    return null;
  }

  if (billingLoading) {
    return (
      <div className="boost-billing-overview">
        <div className="spinthatshit-loader">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  const billing = billingData?.getBillingData;
  const { invoices, subscription, upcomingInvoice } = billing || {};
  const { billingSettings } = userSettings || {};
  const { creditCards, paypal } = billingSettings || {};

  const premium = {
    isPremium,
    premiumVersion,
    isOnFreeTrial,
  };

  const billingProps = {
    invoices,
    subscription,
    upcomingInvoice,
  };

  return (
    <div className="boost-billing-overview">
      <AccountStatus billing={billingProps} premium={premium} />
      {((creditCards && creditCards.length > 0) || paypal) && <BillingInformation creditCards={creditCards} paypal={paypal} />}
      {invoices && invoices.length > 0 && <BillingHistory invoices={invoices} />}
    </div>
  );
};

export default BoostBillingOverview;
